import { useUserGetUserSiteData } from "@crowdcoursing/api/openapi";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { UserGrowthData } from "~ui/templates/AdminTemplate/DashboardStats/UserGrowthData";

const START_DATE_STORAGE_KEY = "userGrowthTab.startDate";

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ days: 30 }),
  };
};

const useUserGrowthDataProps = () => {
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const queryParams = useMemo(
    () => ({
      end: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
    }),
    [timeRange],
  );

  const { data, status } = useUserGetUserSiteData({ queryParams }, { placeholderData: keepPreviousData });

  return {
    data: data ?? null,
    onTimeRangeChange: handleTimeRangeChange,
    status,
    timeRange,
  };
};

export const UserGrowthTab = () => {
  const props = useUserGrowthDataProps();
  return <UserGrowthData {...props} />;
};
